import { inject, Injectable } from '@angular/core';
import { IAlert } from 'src/app/core/models/alert.models';
import { IPolConf, IPolHistory, IPolTestStatus, LIFE_TEST_ID, POL_SEG_HISTORY_INTERVAL } from '../../models/life-check';
import { TestStatus } from '../../models/user-identification';
import { TranslateService } from '@ngx-translate/core';
@Injectable({
	providedIn: 'root',
})
export class PolAudioHistoryService {
	private trans = inject(TranslateService);

	public dataHistoryArray: IPolHistory[] = [];

	public getDataHistoryLastSecs(lastSeconds: number): IPolHistory[] | null {
		return lastSeconds / POL_SEG_HISTORY_INTERVAL > this.dataHistoryArray.length
			? null
			: this.dataHistoryArray.slice(-lastSeconds / POL_SEG_HISTORY_INTERVAL);
	}

	public clearDataHistory(): void {
		this.dataHistoryArray = [];
	}

	public addDataHistory(data: IPolHistory): void {
		this.dataHistoryArray.push(data);

		if (this.dataHistoryArray.length >= 100) {
			this.dataHistoryArray.shift();
		}
	}

	public getEmptyAlert(): IAlert {
		return { label: '', status: 'default' };
	}

	/** Índice del último audio dentro del `promptConfig` del estado activo; -1 si hay que empezar desde el primero. */
	public resolvePromptIndex(state: IPolTestStatus, lastSpeech: string, lastSpeechStateId: string | null): number {
		if (lastSpeechStateId !== state.id) {
			return -1;
		}
		return state.promptConfig.findIndex((conf) => conf.audio === lastSpeech);
	}

	public shouldAdvancePrompt(
		state: IPolTestStatus,
		lastSpeechIndex: number,
		lastSpeech: string,
		lastSpeechStateId: string | null,
		historyLength: number,
	): boolean {
		const speechBelongsToCurrentState =
			lastSpeechStateId === state.id && state.promptConfig.some((conf) => conf.audio === lastSpeech);
		return (
			lastSpeechIndex + state.repeatTime / POL_SEG_HISTORY_INTERVAL < historyLength ||
			!speechBelongsToCurrentState
		);
	}

	public getInitPolMsg(polConf: IPolConf, activeTestId: LIFE_TEST_ID | null): string {
		if (!activeTestId) {
			return '';
		}
		if (activeTestId === LIFE_TEST_ID.MIDDLEFACE) {
			return polConf.middleFace.messages[TestStatus.OnInit] ?? '';
		}
		const lifeTest = polConf.lifeTest.find((test) => test.lifeTestId === activeTestId);
		return lifeTest?.messages[TestStatus.OnInit] ?? '';
	}

	public getTestOnCompleteMsg(polConf: IPolConf, activeTestId: LIFE_TEST_ID | null): string {
		if (!activeTestId) {
			return '';
		}
		if (activeTestId === LIFE_TEST_ID.MIDDLEFACE) {
			return polConf.middleFace.messages[TestStatus.OnComplete] ?? '';
		}
		return polConf.lifeTest.find((test) => test.lifeTestId === activeTestId)?.messages[TestStatus.OnComplete] ?? '';
	}

	public getInitTestMsg(polConf: IPolConf, activeTestId: LIFE_TEST_ID | null): string {
		if (!activeTestId) {
			return '';
		}
		const found =
			activeTestId === LIFE_TEST_ID.MIDDLEFACE
				? polConf.middleFace
				: polConf.lifeTest.find((test) => test.lifeTestId === activeTestId);
		let message: string = '';
		if (found && !found.complete) {
			if (found.status === TestStatus.OnWarning) {
				message = found.messages[TestStatus.OnWarning] ?? found.messages[TestStatus.OnInit] ?? '';
			} else if (found.status === TestStatus.OnInit) {
				message = found.messages[TestStatus.OnInit] ?? '';
			}
		}
		return message;
	}

	public getStatesForActiveTest(polConf: IPolConf, activeTestId: LIFE_TEST_ID | null): IPolTestStatus[] {
		const emptyAlert = this.getEmptyAlert();
		const generalOnFailMsg = polConf.general.messages[TestStatus.OnFail] ?? '';
		const generalOnWarningMsg = polConf.general.messages[TestStatus.OnWarning] ?? '';
		const generalStates: IPolTestStatus[] = [
			{
				id: 'generalOnFail',
				repeatTime: 12,
				promptConfig: [{ audio: generalOnFailMsg, centerText: '', alert: emptyAlert }],
				conditions: () => polConf.general.status === TestStatus.OnFail,
			},
			{
				id: 'generalOnWarning',
				repeatTime: 12,
				promptConfig: [
					{
						audio: generalOnWarningMsg,
						centerText: '',
						alert: { label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.ONWARNING'), status: 'warning' },
					},
				],
				conditions: () => polConf.general.status === TestStatus.OnWarning,
			},
		];

		// Único gesto en OnFail (p. ej. solo approach-face): activeTestId es null pero general
		// ya está en OnWarning/OnFail — hay que exponer generalStates para locutar ONWARNING.
		if (!activeTestId) {
			const generalStatus = polConf.general.status;
			if (generalStatus === TestStatus.OnWarning || generalStatus === TestStatus.OnFail) {
				return generalStates;
			}
			return [];
		}

		if (activeTestId === LIFE_TEST_ID.MIDDLEFACE) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.MIDDLEFACE, emptyAlert),
				...this.getCenteringStates(emptyAlert),
			];
		}

		if (activeTestId === LIFE_TEST_ID.SMILE) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.SMILE, emptyAlert),
				...this.getCenteringStates(emptyAlert),
				this.createSmileNotDetectedState(polConf, LIFE_TEST_ID.SMILE),
			];
		}

		if (activeTestId === LIFE_TEST_ID.APPROACHFACE) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.APPROACHFACE, emptyAlert),
				...this.getCenteringStates(emptyAlert),
			];
		}

		if (activeTestId === LIFE_TEST_ID.TURNRIGHTHEAD) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.TURNRIGHTHEAD, emptyAlert),
				...this.getCenteringStates(emptyAlert),
				this.createTurnNotDetectedState(polConf, LIFE_TEST_ID.TURNRIGHTHEAD),
			];
		}

		if (activeTestId === LIFE_TEST_ID.TURNLEFTHEAD) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.TURNLEFTHEAD, emptyAlert),
				...this.getCenteringStates(emptyAlert),
				this.createTurnNotDetectedState(polConf, LIFE_TEST_ID.TURNLEFTHEAD),
			];
		}

		if (activeTestId === LIFE_TEST_ID.LIFTHEAD) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.LIFTHEAD, emptyAlert),
				...this.getCenteringStates(emptyAlert),
				this.createTurnNotDetectedState(polConf, LIFE_TEST_ID.LIFTHEAD),
			];
		}

		if (activeTestId === LIFE_TEST_ID.LOWERHEAD) {
			return [
				...generalStates,
				this.createCompleteState(polConf, LIFE_TEST_ID.LOWERHEAD, emptyAlert),
				...this.getCenteringStates(emptyAlert),
				this.createTurnNotDetectedState(polConf, LIFE_TEST_ID.LOWERHEAD),
			];
		}

		return [];
	}

	private getCenteringStates(emptyAlert: IAlert): IPolTestStatus[] {
		return [
			this.createHasNoFaceState(),
			this.createIsTooFarState(emptyAlert),
			this.createIsTooCloseState(emptyAlert),
			this.createIsOffCenterState(emptyAlert),
		];
	}

	private createHasNoFaceState(): IPolTestStatus {
		return {
			id: 'hasNoFace',
			repeatTime: 10,
			promptConfig: [
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_1',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.NO_FACE_DETECTED'), status: 'default' },
				},
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_2',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.KEEP_FACE_FITTED'), status: 'default' },
				},
			],
			conditions: () => Boolean(this.getDataHistoryLastSecs(4)?.every((history) => !history.faceDetection)),
		};
	}

	private createIsTooFarState(emptyAlert: IAlert): IPolTestStatus {
		return {
			id: 'isTooFar',
			repeatTime: 10,
			promptConfig: [
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.IS_TOO_FAR_1',
					centerText: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.COME_CLOSER'),
					alert: emptyAlert,
				},
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_2',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.KEEP_FACE_FITTED'), status: 'default' },
				},
				{
					audio: 'LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE'), status: 'default' },
				},
			],
			conditions: () =>
				Boolean(
					this.getDataHistoryLastSecs(2)?.every(
						(history) => history.faceDetection && history.isTooFar && !history.isTooClose,
					),
				),
		};
	}

	private createIsTooCloseState(emptyAlert: IAlert): IPolTestStatus {
		return {
			id: 'isTooClose',
			repeatTime: 10,
			promptConfig: [
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.IS_TOO_CLOSE_1',
					centerText: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.MOVE_AWAY'),
					alert: emptyAlert,
				},
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_2',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.KEEP_FACE_FITTED'), status: 'default' },
				},
				{
					audio: 'LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE'), status: 'default' },
				},
			],
			conditions: () =>
				Boolean(
					this.getDataHistoryLastSecs(2)?.every((history) => history.faceDetection && history.isTooClose),
				),
		};
	}

	private createIsOffCenterState(emptyAlert: IAlert): IPolTestStatus {
		return {
			id: 'isOffCenter',
			repeatTime: 10,
			promptConfig: [
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.MISMATCH_WARNING',
					centerText: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.MISMATCH_WARNING'),
					alert: emptyAlert,
				},
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.HAS_NO_FACE_2',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.KEEP_FACE_FITTED'), status: 'default' },
				},
			],
			conditions: () =>
				Boolean(
					this.getDataHistoryLastSecs(4)?.every(
						(history) =>
							history.faceDetection && history.isOffCenter && !history.isTooFar && !history.isTooClose,
					),
				),
		};
	}

	private createTurnNotDetectedState(polConf: IPolConf, activeTestId: LIFE_TEST_ID): IPolTestStatus {
		const emptyAlert = this.getEmptyAlert();
		return {
			id: 'turnNotDetected',
			repeatTime: 8,
			promptConfig: [
				{
					audio: this.getInitPolMsg(polConf, activeTestId),
					centerText: '',
					alert: emptyAlert,
					keepPrevAlert: true,
				},
				{
					audio: 'LIFE_CHECK.GENERAL.TEXT.TURN_CENTER_AND_TURN',
					centerText: '',
					alert: {
						label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.TURN_CENTER_AND_TURN_ALERT'),
						status: 'default',
					},
				},
				{
					audio: 'LIFE_CHECK.TURN_RIGHT_HEAD.TEXT.ONWARNING',
					centerText: '',
					alert: {
						label: this.trans.instant('LIFE_CHECK.GENERAL.TEXT.TURN_LOOK_AT_SCREEN'),
						status: 'default',
					},
				},
				// {
				// 	audio: 'LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE',
				// 	centerText: '',
				// 	alert: { label: this.trans.instant('LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE'), status: 'default' },
				// },
			],
			conditions: () =>
				Boolean(
					this.getDataHistoryLastSecs(7)?.every(
						(history) =>
							history.faceDetection &&
							!history.isTooClose &&
							!history.isTooFar &&
							!history.isOffCenter &&
							(history.hasInvalidHeadMovements || history.hasInsufficientTransitions),
					),
				),
		};
	}

	private createSmileNotDetectedState(polConf: IPolConf, activeTestId: LIFE_TEST_ID): IPolTestStatus {
		const emptyAlert = this.getEmptyAlert();
		return {
			id: 'smileNotDetected',
			repeatTime: 10,
			promptConfig: [
				{
					audio: this.getInitPolMsg(polConf, activeTestId),
					centerText: '',
					alert: emptyAlert,
					keepPrevAlert: true,
				},
				{
					audio: 'LIFE_CHECK.SMILE.TEXT.ONWARNING',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.SMILE.TEXT.SHOW_TEETH'), status: 'default' },
				},
				{
					audio: 'LIFE_CHECK.SMILE.TEXT.ONWARNING_TRANSITION',
					centerText: '',
					alert: { label: this.trans.instant('LIFE_CHECK.SMILE.TEXT.SLOW_SMILE'), status: 'default' },
				},
				// {
				// 	audio: 'LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE',
				// 	centerText: '',
				// 	alert: { label: this.trans.instant('LIFE_CHECK.MIDDLE_FACE.TEXT.AVOID_GLARE'), status: 'default' },
				// },
			],
			conditions: () =>
				Boolean(
					this.getDataHistoryLastSecs(7)?.every(
						(history) =>
							history.faceDetection &&
							!history.isTooClose &&
							!history.isTooFar &&
							!history.isOffCenter &&
							(history.hasInsufficientEmotion || history.hasInsufficientTransitions),
					),
				),
		};
	}

	private createCompleteState(polConf: IPolConf, activeTestId: LIFE_TEST_ID, emptyAlert: IAlert): IPolTestStatus {
		return {
			id: 'complete',
			repeatTime: 15,
			promptConfig: [
				{
					audio: this.getTestOnCompleteMsg(polConf, activeTestId),
					centerText: '',
					alert: emptyAlert,
				},
			],
			conditions: () => {
				if (!this.getDataHistoryLastSecs(POL_SEG_HISTORY_INTERVAL)?.at(-1)?.complete) {
					return false;
				}
				if (activeTestId === LIFE_TEST_ID.MIDDLEFACE) {
					return true;
				}
				// Solo locutar ONCOMPLETE si queda otro gesto real pendiente (no usar totalLifeTest:
				// puede ser > available_tests y fingiría una prueba detrás).
				return polConf.lifeTest.some((lt) => !lt.complete);
			},
		};
	}
}
